import React, { useState } from 'react';

import Button from '../../../components/ui/Button';

import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const SearchFilters = ({ filters, onFiltersChange, onSaveSearch, isCollapsed, onToggleCollapse }) => {
  const [localFilters, setLocalFilters] = useState(filters);

  const skillsOptions = [
    { value: 'react', label: 'React' },
    { value: 'javascript', label: 'JavaScript' },
    { value: 'typescript', label: 'TypeScript' },
    { value: 'nodejs', label: 'Node.js' },
    { value: 'python', label: 'Python' },
    { value: 'java', label: 'Java' },
    { value: 'csharp', label: 'C#' },
    { value: 'php', label: 'PHP' },
    { value: 'angular', label: 'Angular' },
    { value: 'vue', label: 'Vue.js' },
    { value: 'aws', label: 'AWS' },
    { value: 'docker', label: 'Docker' },
    { value: 'kubernetes', label: 'Kubernetes' },
    { value: 'sql', label: 'SQL' },
    { value: 'mongodb', label: 'MongoDB' }
  ];

  const locationOptions = [
    { value: 'oslo', label: 'Oslo' },
    { value: 'bergen', label: 'Bergen' },
    { value: 'trondheim', label: 'Trondheim' },
    { value: 'stavanger', label: 'Stavanger' },
    { value: 'kristiansand', label: 'Kristiansand' },
    { value: 'tromsø', label: 'Tromsø' },
    { value: 'drammen', label: 'Drammen' },
    { value: 'fredrikstad', label: 'Fredrikstad' }
  ];

  const experienceOptions = [
    { value: 'entry', label: 'Entry Level (0-2 years)' },
    { value: 'mid', label: 'Mid Level (3-5 years)' },
    { value: 'senior', label: 'Senior Level (6-10 years)' },
    { value: 'lead', label: 'Lead/Principal (10+ years)' }
  ];

  const educationOptions = [
    { value: 'high-school', label: 'High School' },
    { value: 'bachelor', label: 'Bachelor\'s Degree' },
    { value: 'master', label: 'Master\'s Degree' },
    { value: 'phd', label: 'PhD' },
    { value: 'bootcamp', label: 'Coding Bootcamp' },
    { value: 'self-taught', label: 'Self-taught' }
  ];

  const languageOptions = [
    { value: 'norwegian', label: 'Norwegian' },
    { value: 'english', label: 'English' },
    { value: 'swedish', label: 'Swedish' },
    { value: 'danish', label: 'Danish' },
    { value: 'german', label: 'German' },
    { value: 'french', label: 'French' },
    { value: 'spanish', label: 'Spanish' }
  ];

  const handleFilterChange = (key, value) => {
    const updatedFilters = { ...localFilters, [key]: value };
    setLocalFilters(updatedFilters);
    onFiltersChange(updatedFilters);
  };

  const handleRangeChange = (key, value) => {
    handleFilterChange(key, parseInt(value));
  };

  const clearAllFilters = () => {
    const clearedFilters = {
      skills: [],
      location: '',
      radius: 50,
      experience: '',
      education: '',
      languages: [],
      salaryMin: 400000,
      salaryMax: 1200000,
      availability: '',
      workType: '',
      workPermit: false,
      remoteWork: false
    };
    setLocalFilters(clearedFilters);
    onFiltersChange(clearedFilters);
  };

  if (isCollapsed) {
    return (
      <div className="lg:hidden fixed top-16 left-0 right-0 z-40 bg-card border-b border-border p-4">
        <Button
          variant="outline"
          onClick={onToggleCollapse}
          iconName="Filter"
          iconPosition="left"
          fullWidth
        >
          Show Filters ({Object.values(localFilters)?.filter(v => v && (Array.isArray(v) ? v?.length > 0 : true))?.length})
        </Button>
      </div>
    );
  }

  return (
    <div className={`bg-card border border-border rounded-xl p-6 ${isCollapsed ? 'fixed inset-0 z-50 lg:relative lg:inset-auto' : ''}`}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-foreground">Search Filters</h2>
        <div className="flex items-center space-x-2">
          <Button variant="ghost" size="sm" onClick={clearAllFilters}>
            Clear All
          </Button>
          <Button variant="ghost" size="sm" onClick={onSaveSearch} iconName="Bookmark">
            Save Search
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="lg:hidden"
            onClick={onToggleCollapse}
            iconName="X"
          />
        </div>
      </div>
      <div className="space-y-6 max-h-[70vh] lg:max-h-none overflow-y-auto">
        {/* Skills */}
        <div>
          <Select
            label="Skills"
            placeholder="Select required skills"
            options={skillsOptions}
            value={localFilters?.skills}
            onChange={(value) => handleFilterChange('skills', value)}
            multiple
            searchable
            clearable
          />
        </div>

        {/* Location & Radius */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Location"
            placeholder="Select location"
            options={locationOptions}
            value={localFilters?.location}
            onChange={(value) => handleFilterChange('location', value)}
            clearable
          />
          
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Radius: {localFilters?.radius} km
            </label>
            <input
              type="range"
              min="10"
              max="200"
              value={localFilters?.radius}
              onChange={(e) => handleRangeChange('radius', e?.target?.value)}
              className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer slider"
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>10 km</span>
              <span>200 km</span>
            </div>
          </div>
        </div>

        {/* Experience & Education */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Experience Level"
            placeholder="Select experience"
            options={experienceOptions}
            value={localFilters?.experience}
            onChange={(value) => handleFilterChange('experience', value)}
            clearable
          />
          
          <Select
            label="Education"
            placeholder="Select education"
            options={educationOptions}
            value={localFilters?.education}
            onChange={(value) => handleFilterChange('education', value)}
            clearable
          />
        </div>

        {/* Languages */}
        <div>
          <Select
            label="Languages"
            placeholder="Select languages"
            options={languageOptions}
            value={localFilters?.languages}
            onChange={(value) => handleFilterChange('languages', value)}
            multiple
            searchable
            clearable
          />
        </div>

        {/* Salary Range */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-4">
            Salary Range: {localFilters?.salaryMin?.toLocaleString('no-NO')} - {localFilters?.salaryMax?.toLocaleString('no-NO')} NOK
          </label>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-muted-foreground">Minimum</label>
              <input
                type="range"
                min="300000"
                max="1500000"
                step="50000"
                value={localFilters?.salaryMin}
                onChange={(e) => handleRangeChange('salaryMin', e?.target?.value)}
                className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer slider"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Maximum</label>
              <input
                type="range"
                min="300000"
                max="1500000"
                step="50000"
                value={localFilters?.salaryMax}
                onChange={(e) => handleRangeChange('salaryMax', e?.target?.value)}
                className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer slider"
              />
            </div>
          </div>
        </div>

        {/* Work Preferences */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-foreground">Work Preferences</h3>
          
          <div className="space-y-3">
            <Checkbox
              label="Remote work available"
              checked={localFilters?.remoteWork}
              onChange={(e) => handleFilterChange('remoteWork', e?.target?.checked)}
            />
            
            <Checkbox
              label="Has work permit"
              checked={localFilters?.workPermit}
              onChange={(e) => handleFilterChange('workPermit', e?.target?.checked)}
            />
          </div>
        </div>

        {/* Availability */}
        <div>
          <Select
            label="Availability"
            placeholder="Select availability"
            options={[
              { value: 'immediate', label: 'Available immediately' },
              { value: '2weeks', label: 'Available in 2 weeks' },
              { value: '1month', label: 'Available in 1 month' },
              { value: '3months', label: 'Available in 3 months' }
            ]}
            value={localFilters?.availability}
            onChange={(value) => handleFilterChange('availability', value)}
            clearable
          />
        </div>
      </div>
      <div className="lg:hidden mt-6 pt-6 border-t border-border">
        <Button onClick={onToggleCollapse} fullWidth>
          Apply Filters
        </Button>
      </div>
    </div>
  );
};

export default SearchFilters;